<template>
  <q-popover ref="popover" class="time-tracking__project__popover" @show="handleShow" @hide="handleHide">
    <div class="q-pa-md">
      <q-search
        v-model="searchQuery"
        inverted-light
        color="blue-grey-1"
        class="time-tracking__project__search"
        :debounce="defaultSearchDebounce"
        @input="handleSearch"
      />

      <div class="time-tracking__project__list q-mt-md">
        <template v-if="projects.length">
          <q-infinite-scroll
            ref="infiniteScroll"
            inline
            style="height: 200px; overflow: auto;"
            :handler="handleLoadMore"
          >
            <div class="q-mb-xs">
              <div
                class="time-tracking__project hover-highlight q-py-xs q-px-sm row items-center cursor-pointer"
                @click="handleSelectProject(null)"
              >
                <div class="time-tracking__project__color-indicator bg-faded circle"></div>
                <span class="q-subheading text-italic text-faded on-right">{{ $t('without_project') }}</span>
              </div>
            </div>

            <div
              v-for="project in projects"
              :key="project.id"
              class="q-mb-xs"
            >
              <project-indicator
                clickable
                :project="project"
                :active="isActiveProject(project)"
                @click="handleSelectProject(project)"
              />
            </div>
          </q-infinite-scroll>
        </template>

        <template v-else>
          <div class="fit row justify-center items-center">
            <span class="text-faded">{{ $t('no_projects_found') }}</span>
          </div>
        </template>
      </div>

      <div>
        <q-btn
          flat
          dense
          no-caps
          icon="add"
          color="faded"
          :label="$tc('project')"
          @click="handleAddProject"
        />
      </div>
    </div>

    <project-modal ref="createProjectModal" @save="handleSaveProject"/>
  </q-popover>
</template>

<script>
  import ProjectIndicator from './ProjectIndicator.vue'
  import ProjectModal from './Projects/ProjectModal.vue'
  import { ProjectsClient } from 'src/api/Projects'
  import changeCase from 'change-case-object'
  import { createErrorFromResponse } from 'src/utils/error'
  import debounceMixin from 'src/utils/mixins/debounce'

  export default {
    inject: ['$validator'],

    props: {
      activeProject: {
        type: Object,
        default: null
      }
    },

    data () {
      return {
        projects: [],
        projectsMeta: null,
        searchQuery: null
      }
    },

    computed: {
      isSearch () {
        return !!this.searchQuery
      },

      reachedProjectsEnd () {
        return this.projectsMeta ? this.currentPage === this.projectsMeta.pagination.total_pages : false
      },

      currentPage () {
        return this.projectsMeta ? this.projectsMeta.pagination.current_page : 0
      }
    },

    methods: {
      handleSearch () {
        if (!this.isSearch) {
          return this.fetchProjectsList(1, true)
            .then(this.resetList)
        }

        this.fetchProjectSearchResults(this.searchQuery, 1, true)
          .then(this.resetList)
      },

      handleSelectProject (project) {
        this.$emit('select-project', project)

        this.$refs.popover.hide()
      },

      handleAddProject () {
        this.$refs.createProjectModal.show()

        this.$refs.popover.hide()
      },

      handleSaveProject (project) {
        return new ProjectsClient()
          .filter(['active', '=', 'true'])
          .include(['customer'])
          .create(changeCase.snake(project))
          .then(response => {
            return response.data.data
          })
          .then(this.handleSelectProject)
          .catch(createErrorFromResponse)
      },

      handleShow () {
        this.fetchProjectsList(1, true)
      },

      handleHide () {
        this.reset()
      },

      handleLoadMore (index, done) {
        if (this.reachedProjectsEnd) {
          return done(true)
        }

        const nextPage = this.currentPage + 1

        const fetch = this.isSearch ? this.fetchProjectSearchResults(this.searchQuery, nextPage) : this.fetchProjectsList(nextPage)

        fetch.then(done)
      },

      fetchProjectsList (page, reset = false) {
        return new ProjectsClient()
          .filter(['active', '=', 'true'])
          .sort('id:desc')
          .include(['customer'])
          .page(page)
          .list()
          .then(response => {
            const projects = reset ? [] : this.projects

            this.projects = projects.concat(response.data.data)
            this.projectsMeta = response.data.meta
          })
      },

      fetchProjectSearchResults (query, page, reset = false) {
        return new ProjectsClient()
          .filter(['active', '=', 'true'])
          .include(['customer'])
          .page(page)
          .search(query)
          .then(response => {
            const projects = reset ? [] : this.projects

            this.projects = projects.concat(response.data.data)
            this.projectsMeta = response.data.meta
          })
      },

      isActiveProject (project) {
        return this.activeProject && this.activeProject.id === project.id
      },

      resetList () {
        if (!this.$refs.infiniteScroll) {
          return
        }

        this.$refs.infiniteScroll.$el.scrollTop = 0

        this.$refs.infiniteScroll.reset()
        this.$refs.infiniteScroll.resume()
      },

      reset () {
        this.searchQuery = null
        this.resetList()
      }
    },

    components: {
      ProjectIndicator,
      ProjectModal
    },

    mixins: [
      debounceMixin
    ]
  }
</script>

<style lang="stylus">
  @import '~variables'

  .time-tracking
    &__project
      &__popover
        width: 270px
      &__search
        box-shadow: none
        border-radius: 20px
      &__list
        height: 200px
</style>
